'use client';

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/auth-context';
import {
  getUserWritingTasks,
  WritingTask,
  WritingTaskType,
  deleteWritingTask,
} from '@/lib/firebase/writing';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import Image from 'next/image';

interface TaskListProps {
  onSelectTask: (task: WritingTask) => void;
  selectedTaskId: string | null;
  filterTaskType?: WritingTaskType | null;
}

export function TaskList({
  onSelectTask,
  selectedTaskId,
  filterTaskType = null,
}: TaskListProps) {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<WritingTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteTaskId, setDeleteTaskId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      setTasks([]);
      setLoading(false);
      return;
    }

    const fetchTasks = async () => {
      setLoading(true);
      setError(null);
      try {
        const userTasks = await getUserWritingTasks(user.uid);
        const filteredTasks = filterTaskType
          ? userTasks.filter((task) => task.taskType === filterTaskType)
          : userTasks;
        setTasks(filteredTasks);
      } catch (err) {
        console.error('Failed to fetch writing tasks:', err);
        setError('Failed to load tasks. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, [user, filterTaskType]);

  const formatTimestamp = (timestamp: Timestamp | Date | string): string => {
    if (!timestamp) return 'Unknown date';
    try {
      const date =
        timestamp instanceof Timestamp
          ? timestamp.toDate()
          : typeof timestamp === 'string'
          ? new Date(timestamp)
          : timestamp instanceof Date
          ? timestamp
          : new Date();

      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return 'Invalid date';
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, taskId: string) => {
    e.stopPropagation(); // Prevent triggering the parent onClick
    setDeleteTaskId(taskId);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!user || !deleteTaskId) return;

    setIsDeleting(true);
    try {
      await deleteWritingTask(user.uid, deleteTaskId);
      setTasks(tasks.filter((task) => task.id !== deleteTaskId));
      toast.success('Item deleted successfully');
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete item');
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
      setDeleteTaskId(null);
    }
  };

  if (loading) {
    return (
      <div className='space-y-2 px-2'>
        <Skeleton className='h-14 w-full rounded-md' />
        <Skeleton className='h-14 w-full rounded-md' />
        <Skeleton className='h-14 w-full rounded-md' />
      </div>
    );
  }

  if (error) {
    return <p className='text-red-600 text-sm px-4'>{error}</p>;
  }

  if (!tasks.length) {
    // Format task type properly (e.g., "task2" -> "Task 2")
    const formatTaskType = (taskType: string | null): string => {
      if (!taskType) return '';
      if (taskType === 'task1') return 'Task 1';
      if (taskType === 'task2') return 'Task 2';
      return taskType.charAt(0).toUpperCase() + taskType.slice(1);
    };

    return (
      <p className='text-sm text-center text-gray-500 py-4 px-4'>
        No saved tasks found
        {filterTaskType ? ` for ${formatTaskType(filterTaskType)}` : ''}.
      </p>
    );
  }

  return (
    <>
      <ScrollArea className='h-full w-full'>
        <div className='space-y-1 px-2 py-2'>
          {tasks.map((task) => (
            <div
              key={task.id}
              className={cn(
                'relative w-full p-3 border rounded-md text-left transition-colors duration-150 ease-in-out group',
                'hover:bg-muted/80 focus-within:ring-1 focus-within:ring-ring focus-within:bg-muted',
                selectedTaskId === task.id
                  ? 'bg-muted font-semibold ring-1 ring-ring'
                  : 'bg-transparent border-transparent'
              )}
            >
              <div
                role='button'
                tabIndex={0}
                className='w-full pr-8'
                onClick={() => onSelectTask(task)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onSelectTask(task);
                  }
                }}
              >
                <p className='text-sm font-medium mb-1.5 break-words'>
                  {task.prompt ||
                    (task.taskType === 'task1'
                      ? 'No description'
                      : 'No prompt')}
                </p>
                {task.taskType === 'task1' && task.imageUrl && (
                  <div className='my-2 rounded-md overflow-hidden border border-muted max-w-[150px] max-h-[100px]'>
                    <Image
                      src={task.imageUrl}
                      alt={task.prompt || 'Task 1 visual'}
                      className='object-contain w-full h-full'
                      width={150}
                      height={100}
                    />
                  </div>
                )}
                <p className='text-xs text-gray-500'>
                  Saved {formatTimestamp(task.lastSaved)}
                </p>
              </div>

              {/* Delete button */}
              <Button
                variant='ghost'
                size='icon'
                className='absolute top-2 right-2 transition-colors focus:opacity-100'
                onClick={(e) => handleDeleteClick(e, task.id)}
                aria-label='Delete'
              >
                <Trash2 className='h-4 w-4 text-destructive' />
              </Button>
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this item and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
